import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/types";
import type { ToolCategory, ToolCondition } from "@/lib/types";

interface ListToolPageProps {
	onBack: () => void;
	onSuccess: (toolId: string) => void;
}

export function ListToolPage({ onBack, onSuccess }: ListToolPageProps) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [category, setCategory] = useState<ToolCategory>("hand-tools");
	const [condition, setCondition] = useState<ToolCondition>("good");
	const [dailyRate, setDailyRate] = useState("");
	const [deposit, setDeposit] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [success, setSuccess] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!name || !description || !dailyRate || !deposit) return;

		setSubmitting(true);
		try {
			const tool = await api.createTool({
				name,
				description,
				category,
				condition,
				dailyRate: Number(dailyRate),
				deposit: Number(deposit),
			});
			setSuccess(true);
			setTimeout(() => onSuccess(tool.id), 1500);
		} catch (e) {
			console.error("Failed to create tool:", e);
		} finally {
			setSubmitting(false);
		}
	}

	if (success) {
		return (
			<div className="max-w-lg mx-auto py-16 text-center">
				<div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
					<Check className="w-8 h-8 text-emerald-600" />
				</div>
				<h2 className="text-xl font-bold">Tool listed!</h2>
				<p className="text-muted-foreground mt-2">
					Your tool is now visible to neighbors in your area.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-2xl mx-auto space-y-6">
			<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
				<ArrowLeft className="w-4 h-4" />
				Back
			</Button>

			<div>
				<h1 className="text-xl font-bold">List a tool for rent</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Help your neighbors and earn money from tools you're not using
				</p>
			</div>

			<Card>
				<CardContent className="p-6">
					<form onSubmit={handleSubmit} className="space-y-5">
						<div className="space-y-2">
							<Label htmlFor="name">Tool name</Label>
							<Input
								id="name"
								placeholder="e.g., DeWalt 20V Cordless Drill"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="description">Description</Label>
							<Textarea
								id="description"
								placeholder="Describe the tool, its features, and what it's best used for..."
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={3}
								required
							/>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label>Category</Label>
								<Select
									value={category}
									onValueChange={(v) => setCategory(v as ToolCategory)}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{Object.entries(CATEGORY_LABELS).map(([key, label]) => (
											<SelectItem key={key} value={key}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label>Condition</Label>
								<Select
									value={condition}
									onValueChange={(v) => setCondition(v as ToolCondition)}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{Object.entries(CONDITION_LABELS).map(([key, label]) => (
											<SelectItem key={key} value={key}>
												{label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="rate">Daily rate ($)</Label>
								<Input
									id="rate"
									type="number"
									min="1"
									placeholder="15"
									value={dailyRate}
									onChange={(e) => setDailyRate(e.target.value)}
									required
								/>
								<p className="text-xs text-muted-foreground">
									What you'll earn per day
								</p>
							</div>
							<div className="space-y-2">
								<Label htmlFor="deposit">Security deposit ($)</Label>
								<Input
									id="deposit"
									type="number"
									min="0"
									placeholder="80"
									value={deposit}
									onChange={(e) => setDeposit(e.target.value)}
									required
								/>
								<p className="text-xs text-muted-foreground">
									Refunded when returned safely
								</p>
							</div>
						</div>

						<Button type="submit" className="w-full" disabled={submitting}>
							{submitting ? "Listing..." : "List tool for rent"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
